import type { LocationQueryValue } from 'vue-router'

/** Bounds shared by the giftcard buy box and the personalization amount field. */
export const GIFTCARD_MIN_AMOUNT = 15
export const GIFTCARD_MAX_AMOUNT = 200

/**
 * Resolve a giftcard amount from a URL query value (e.g. `?amount=50`).
 *
 * Returns a whole-euro amount clamped into `[min, max]`, or `null` when the
 * value is missing, non-numeric, or zero — so callers can fall back to their
 * own default. Negative values are treated as garbage below the minimum and
 * clamp up to `min` rather than being rejected. Used to carry the amount
 * selected in the giftcard buy box over to the personalization page and
 * prefill its amount field.
 */
export const resolveGiftcardAmountFromQuery = (
  raw: LocationQueryValue | LocationQueryValue[] | undefined,
  {
    min = GIFTCARD_MIN_AMOUNT,
    max = GIFTCARD_MAX_AMOUNT,
  }: { min?: number; max?: number } = {}
): number | null => {
  const value = Number(Array.isArray(raw) ? raw[0] : raw)

  if (!Number.isFinite(value) || value === 0) return null

  return Math.min(Math.max(Math.round(value), min), max)
}
